import { useState, useEffect, useContext, ReactElement, CSSProperties } from "react";
import { ToastContext } from "../../context/ToastContext";
import { Form, Input, Image as AntImage, Typography, Timeline, Tag, Button, Tooltip, Modal, Card } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import Booking, { ExternalBookingStatus } from "../../models/Booking";
import Subform from "../Global/Subform";
import TspService from "../../services/crud/TspService";
import Tsp from "../../models/Tsp";
import BookingExternalService, { BookingExternal, ReturnBookingPayload } from "../../services/crud/BookingExternalService";
import { EC2B_FORM_FIELD_TYPES, mapFieldsToAnswers } from "../../models/EC2BForm";
import { OverlayContext } from "../../context/OverlayContext";
import { Modes } from "../../Modes";
import { NavigationContext } from "../../context/NavigationContext";
import { capitalizeFirstLetterLowerRest } from "../../helpers/string";
import { formatDateTime } from "../../utils/dates";
import UserFeedback from "../../models/UserFeedback";
import UserFeedbackService from "../../services/crud/UserFeedbackService";
import RatingStars from "../RatingStars";
import { PaymentStage } from "../../models/CostSpecificationRow";
import { WalletFormButton } from "../WalletFormButton";
import Account from "../../models/Account";
import { useNavigate } from "react-router-dom";
import TspReceipt from "../../models/TSpReceipt";
import { getAccountRoute } from "../../helpers/account-route";
import Image from "../../models/Image";
import ImagesService from "../../services/crud/ImageService";


// eslint-disable-next-line import/no-anonymous-default-export

export type BookingModalViewType = { id?; form?; bookingId: number; update?; setBookings?: any };
const BookingModalView = ({ id, form, update, bookingId, setBookings }: BookingModalViewType) => {
  const { loading, loadMessage } = useContext(ToastContext);
  const { mode } = useContext(NavigationContext);
  const { closeModal } = useContext(OverlayContext);
  const [tsp, setTsp] = useState<Tsp|null>(null);
  const [itemImage, setItemImage] = useState<Image|null>(null);
  const [booking, setBooking] = useState<BookingExternal|null>(null);
  const [userFeedback, setUserFeedback] = useState<UserFeedback|null>(null);
  let navigate = useNavigate();

  useEffect(() => {
    loadMessage("Loading...", async () => {
      const [loadedBooking, loadedUserFeedback] = await Promise.all([
        BookingExternalService.read(bookingId),
        UserFeedbackService.getUserFeedback(bookingId).catch(() => null),
      ]);

      setBooking(loadedBooking);
      setUserFeedback(loadedUserFeedback);

      TspService.getTsp(loadedBooking.tspId).then((data) => setTsp(data));
      if (loadedBooking.itemImageId) {
        ImagesService.getImage(loadedBooking.itemImageId).then((data) => setItemImage(data));
      } else {
        setItemImage(null);
      }
    });
    return () => {
      setTsp(undefined);
      setBooking(null);
      setUserFeedback(null);
    }
  }, [bookingId])

  const navigateTo = (route: String) => {
    closeModal();
    navigate(route as any, {});
  }

  const navigateToAccount = (account?: Account) => {
    let route: String|null = account ? getAccountRoute(account) : null
    if (route !== null) {
      navigateTo(route);
    }
  }

  const textLinkStyle: CSSProperties = {textAlign: 'left', padding: 0, color: 'black', fontWeight: 300};

  const handleReturnBooking = () => {
    Modal.confirm({
      title: 'Return booking?',
      content: 'Email notifications to the end user may be triggered.',
      onOk: () => {
        returnBooking();
      }
    })
  };

  const returnBooking = () => {
    if (!booking) {
      return;
    }
    loadMessage("loading", async () => {
      const payload: ReturnBookingPayload = {
        tspKey: booking.tspKey,
        bookingId: booking.bookingId,
        langKey: "en",
      };
      const returnConfig = await BookingExternalService.returnBookingConfig(booking.bookingId, booking.tspKey);
      if (returnConfig?.skipForm || !returnConfig?.form) await BookingExternalService.returnBooking(payload);
      else {
        // Automatically marks all check_box fields as true
        const form = returnConfig.form;
        const fields = form?.fields;
        fields
          .filter((field) => field.type === EC2B_FORM_FIELD_TYPES.CONFIRM_CHECK)
          .forEach((field) => field.options.map((option) => (option.value = true)));
        const answers = mapFieldsToAnswers(fields);
        const formResponse = { answers: answers };
        const returnBookingPayload = { ...payload, formResponse: formResponse };
        await BookingExternalService.returnBooking(returnBookingPayload);
        closeModal();
      }

      const updatedBooking = await BookingExternalService.read(booking.id);

      setBookings((prev) => {
        const bookings = [...prev];
        const newBooking = bookings.find((b) => b.id === booking.id);
        newBooking.status = updatedBooking.status;
        return bookings;
      });
      return "returned booking";
    });
  };

  const returnButtonEnabled = (() => {
    return [ExternalBookingStatus.LEASE_STARTED, ExternalBookingStatus.LEASE_EXTENDED].includes(booking?.status as ExternalBookingStatus);
  })();

  const returnButton = (<Form.Item>
    <h2 style={{ color: 'red', borderBottom: '1px solid red' }}>Danger zone</h2>
      <p>Med denna knapp kan du tvinga en bokning att returneras på samma sätt som om användaren själv avslutar den. Dvs även om användaren inte fysiskt har returnerat fordonet ännu, så förlorar användaren tillgång till nyckeln och vi kan behöva skicka ut någon för att få rätt på fordonet då.</p>
      <p>Om det är en bokning med t.ex. km-avgift så görs en avläsning vid denna tidpunkt och användaren kan sen betala den i appen eller på faktura.</p>
      <Tooltip
        title={
          returnButtonEnabled
            ? "Returns the vehicle and allows other users to start lease."
            : "You can only return bookings that is not returned or completed."
        }
      >
      <Button disabled={!returnButtonEnabled} loading={loading} onClick={handleReturnBooking}>
        Return booking
      </Button>
    </Tooltip>
  </Form.Item>)

  function addKr(price: number|null) {
    if (price !== null && price !== undefined) {
      return `${price} kr`;
    }
    return null;
  }

  function formatCostDetail(price: String|null, priceFormWallet: String|null): String {
    if (price == null) {
      return 'saknas';
    }
    var s = price;
    if (Modes.SYSTEM_ADMIN === mode || Modes.DEVELOPER === mode) {
      s += ` (${priceFormWallet} from wallet)`;
    }
    return s;

  }

  function initialCostStr(): String {
    return formatCostDetail(addKr(booking?.initialPriceInKr), addKr(booking?.initialPriceFromWalletInKr));
  }

  function returnCostStr(): String {
    return formatCostDetail(addKr(booking?.returnPriceInKr), addKr(booking?.returnPriceFromWalletInKr));
  }

  function costSpecification(stage: PaymentStage): ReactElement {
    const costSpecRows = booking?.bookingCostSpecificationRows ?? [];
    return (
      <ul
        style={{marginBottom: 0}}
      >
        {
        costSpecRows
          .filter((row) => row.paymentStage === stage)
          .map((row) => <li>{`${row.label}: ${row.formattedValue} ${row.unit}`}</li>)
        }
      </ul>
    )
  }

  const downloadReceipt = (receipt: TspReceipt): void => {
    const blob = new Blob([receipt.html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const safeFilename = receipt.emailSubject.replace(/[<>:"/\\|?*]+/g, "");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFilename || "receipt"}.html`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const content = (
    <Form>
      <Form.Item noStyle name="id">
        <Input type="hidden" />
      </Form.Item>

      <Subform
        content={
          <>
            <div style={{
              position: "absolute", top: 120, right: 80, width: 120,
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end',
            }}>
              <div style={{padding: 16, border: "1px solid #f5f5f5"}} >
                <AntImage src={tsp?.logo} preview={false} />
              </div>
              { itemImage && (
                <div style={{padding: 16, marginTop: 8, border: "1px solid #f5f5f5"}}>
                  <AntImage src={Image.getSrcUrl(itemImage)} preview={false} style={{cursor: 'pointer'}} onClick={() => navigateTo(`/images/${itemImage.id}`)} />
                </div>
              )}
            </div>
            <Form.Item name="bookingLabel" label="Resource">
              <Typography.Text>
                {booking?.bookingLabel}
              </Typography.Text>
            </Form.Item>
            <Form.Item name="user" label="User">
              <Button type="link" onClick={() => {navigateTo(`/users/${booking?.user?.id}`)}} style={textLinkStyle}>
                {booking?.user?.username} (id: {booking?.user?.id})<br/>
                {booking?.user?.firstName} {booking?.user?.lastName}<br/>
              </Button>
            </Form.Item>
            <Form.Item name="account" label="Account">
              <Button type="link" onClick={() => {navigateToAccount(booking?.account)}} style={textLinkStyle}>
                {booking?.account.name}
              </Button>
            </Form.Item>
            <Form.Item name="id" label="EC2B booking id">
              <Typography.Text>{booking?.id}</Typography.Text>
            </Form.Item>
            <Form.Item name="tspBookingId" label={"TSP booking id".replace("TSP", tsp?.name ?? 'TSP')}>
              <Typography.Text>{booking?.bookingId ?? "-"}</Typography.Text>
            </Form.Item>
            <Form.Item name="price" label="Price">
              <Typography.Text>Total: {booking?.totalPriceInKr + ' kr'}</Typography.Text>
              { booking?.initialPriceInKr !== null || booking?.returnPriceInKr !== null ?
                <Typography.Text
                  style={{display: "block", marginLeft: "1em"}}
                >
                  Booking price: {initialCostStr()} <br/>
                  {costSpecification(PaymentStage.INITIAL)}
                  Return price: {returnCostStr()} <br/>
                  {costSpecification(PaymentStage.RETURN)}
                </Typography.Text>
                : <></>
              }
            </Form.Item>
            <Form.Item name="receipts" label="Receipts">
              { (booking?.tspReceipts ?? []).map((r: TspReceipt) => {
                return <Card
                  size="small"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography.Text><b>Subject: </b>{ r.emailSubject }</Typography.Text><br/>
                  <Button onClick={() => downloadReceipt(r)}>
                    Download receipt
                  </Button>
                  { /*
                  <iframe
                    srcDoc={r.html}
                    sandbox=""
                    style={{width: "100%", height: '200px', border: 0}}
                  />
                  */}
                </Card>;
              }) }
              { (booking?.tspReceipts ?? []).length === 0 ? <Typography.Text style={{fontStyle: 'italic'}}>Inga kvitton</Typography.Text> : <></> }
            </Form.Item>
            <Form.Item name="price" label="Booking">
              <div style={{ display: "flex", flexDirection: "row", justifyContent: "start", alignItems: "start" }}>
                <div style={{ marginRight: 40 }}>
                  <Tag color={booking ? Booking.STATUS_COLORS[booking.status.toLowerCase()] : "transparent"}>{capitalizeFirstLetterLowerRest(booking?.status)}</Tag>
                  {booking?.isLate && <Tag color="red">Late</Tag>}
                </div>
                <div>
                  Ref: <Typography.Text code>{booking?.referenceId}</Typography.Text>
                </div>
              </div>
            </Form.Item>
            {Modes.SYSTEM_ADMIN === mode || Modes.DEVELOPER === mode ? <Form.Item name="tsp key" label="TSP key">
              <Button type="link" onClick={() => {navigateTo(`/tsps/${booking?.tspId}`)}} style={textLinkStyle}>
                {booking?.tspKey}
              </Button>
            </Form.Item> : <></>}
            {booking?.account?.type === Account.types.PRIVATE && (Modes.SYSTEM_ADMIN === mode || Modes.DEVELOPER === mode)
              ? <Form.Item name="wallet" label="User wallet">
              <WalletFormButton
                userId={booking?.userId}
                actorBookingsExternalId={bookingId}
                buttonLabel="Wallet"
              />
            </Form.Item> : <></>}
            {Modes.SYSTEM_ADMIN === mode || Modes.DEVELOPER === mode ? <Form.Item name="feedback" label="User feedback">
              { userFeedback !== null
                ? (<>
                    <RatingStars rating={userFeedback.rating}></RatingStars>
                    <div><Typography.Text>{userFeedback.feedbackText}</Typography.Text></div>
                    {
                      userFeedback.freshdeskTicketId && (
                        <Button
                          href={UserFeedback.getFreshdeskAdminTicketUrl(userFeedback)}
                          target="_blank"
                          type="primary"
                          size="small"
                          icon={ <LinkOutlined /> }
                          style={{marginTop: '4px'}}
                        >Freshdesk Ticket #{userFeedback.freshdeskTicketId}</Button>
                      )
                    }
                  </>)
                : (<></>)
              }
            </Form.Item> : <></>}

            <Form.Item>
              <Timeline mode="left" style={{ position: "relative", left: -145, width: 780 }}>
                {
                  booking
                  ? BookingExternalService.getBookingTimeLine(booking).map((item) => (
                      <Timeline.Item color={item.color ?? 'green'} label={item.label}>
                        {formatDateTime(item.date)}
                      </Timeline.Item>
                    ))
                  : <></>
                }
              </Timeline>
            </Form.Item>

            {Modes.SYSTEM_ADMIN === mode || Modes.DEVELOPER === mode ? returnButton : <></>}
          </>
        }
      />
    </Form>
  );

  return content;
};
export default BookingModalView;
